/**
 * How a failed fetch of the signed-in user's own record should be handled.
 *
 * - `already-signed-out`: the PocketBase client's `afterSend` hook has already
 *   cleared the auth store (it does that on 401) and fired `onChange`.
 * - `sign-out`: the stored session is unusable; clear it so the app redirects
 *   to the login page.
 * - `keep-session`: the backend or network hiccuped; stay signed in and fall
 *   back to the auth record already in the store.
 */
export type UserFetchOutcome = "already-signed-out" | "sign-out" | "keep-session";

/**
 * Classifies a failure to load the signed-in user's own record.
 *
 * The 404 case is the one this was written for (FANL-1433). PocketBase's auth
 * middleware ignores an unusable token and serves the request as a *guest*, and
 * a request that fails a collection rule is answered with
 * "The requested resource wasn't found." — so a lapsed session got 404, never
 * 401, on every read. Treating that as transient kept the session alive against
 * a token the server rejects: the app rendered as signed-in while every fetch
 * 404'd and nothing ever redirected to the login page.
 *
 * The record now comes from /app/me, behind authz.RequireAuth, which answers a
 * token it cannot resolve with a real 401 — so the ambiguous 404 should no
 * longer arrive at all. The branch stays because the reasoning still holds if
 * one does: the token was issued by the server, so "not found" can only mean
 * the session is over (expired, token key rotated, or the account is gone).
 *
 * `tokenIsValid` is `pb.authStore.isValid`, a local check of the JWT's `exp` —
 * it catches a plainly expired token without needing the server's verdict.
 */
export function classifyUserFetchFailure(status: number, tokenIsValid: boolean): UserFetchOutcome {
  if (status === 401) return "already-signed-out";
  // 403 on your own record means the account is in a broken state; afterSend
  // does not clear on 403 (permission denied ≠ invalid token), so do it here.
  if (status === 403 || status === 404) return "sign-out";
  if (!tokenIsValid) return "sign-out";
  return "keep-session";
}

/**
 * True when the stored session should be dropped without even asking the
 * server: there is a remembered account but its token has expired. Used on
 * mount and while the tab stays open, since an expired token would otherwise
 * only surface as an unexplained 404 on every request.
 */
export function isStoredSessionExpired(hasRecord: boolean, tokenIsValid: boolean): boolean {
  return hasRecord && !tokenIsValid;
}
